/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { demandSourceService } from '../services';
import { useEditImpactGuard, type EditImpactTarget } from './useEditImpactGuard';
import type { QuickTestResult, TestRunContext, DemandSourceTestEvidence } from '../services/demand-source.service';
import { DEFAULT_DEMAND_SOURCE_FILTER } from '../utils/demand-source-list-filter';
import type { ClonedArtifactIdentity, DemandSource, DemandSourceListFilter } from '../types';

export const DEMAND_SOURCES_KEY = 'demand-sources';

/** A demand source as an inheritance target: its type, and the key its own reads live under (`P3a`). */
export const DEMAND_SOURCE_INHERITANCE = { type: 'demand_source', queryRoot: DEMAND_SOURCES_KEY } as const;

/**
 * The list, for a filter. **The default is the unfiltered first page**; the Demand Sources page passes
 * the filter its URL states (`LP12`), which is the only place a list filter lives.
 */
/** `viewerId` answers the Owner filter's *Mine* (`matchesOwnerFilter`); `null` where nobody asks it. */
export function useDemandSources(
  filter: DemandSourceListFilter = DEFAULT_DEMAND_SOURCE_FILTER,
  viewerId: string | null = null,
) {
  return useQuery({
    queryKey: [DEMAND_SOURCES_KEY, filter, viewerId],
    queryFn: () => demandSourceService.list(filter, viewerId),
  });
}

/**
 * The vendor templates (`#1090`).
 *
 * Keyed **under** `DEMAND_SOURCES_KEY` so every existing invalidation refreshes it — a template is
 * a demand-source artifact, saved through the same endpoints, so a list keyed independently would
 * go stale the moment somebody edited one. Same reason the inheritance query is keyed there.
 */
export function useDemandSourceTemplates() {
  return useQuery({
    queryKey: [DEMAND_SOURCES_KEY, 'templates'],
    queryFn: () => demandSourceService.listTemplates(),
  });
}

export function useDemandSource(id: string | number) {
  return useQuery({
    queryKey: [DEMAND_SOURCES_KEY, id],
    queryFn: () => demandSourceService.get(id),
    enabled: !!id,
  });
}

/**
 * What deleting this source would take away from the sources that extend it (`P3a`).
 *
 * A question, so it writes nothing and 404s nowhere — an empty list is the ordinary answer. It is
 * **not** what makes the delete safe: the server detaches every child first, so each keeps every
 * value it was inheriting. The dialog exists to say the *link* is going.
 */
export function useDemandSourceDeletionImpact(id: string | number, enabled = true) {
  return useQuery({
    queryKey: [DEMAND_SOURCES_KEY, id, 'deletion-impact'],
    queryFn: () => demandSourceService.deletionImpact(id),
    enabled: !!id && enabled,
  });
}

/**
 * Duplicate a source (`P3a`).
 *
 * Invalidates the whole list rather than inserting the copy: an **extended** copy's stored form is a
 * layer, so what the operator will see is the *resolved* document the server computed — reading it
 * back is the only way to show what the copy actually is. Optimistically inserting the payload we
 * sent would show a fully-populated artifact that does not match what was stored, which is the
 * class of bug the resolution mark already exists to prevent (#1002: *a value reads as proof*).
 */
export function useCloneDemandSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      copy,
      document,
    }: {
      id: string | number;
      copy: ClonedArtifactIdentity;
      /** The editor's unsaved draft (`ST12` item 2) — absent from every list caller. */
      document?: Partial<DemandSource>;
    }) => demandSourceService.clone(id, copy, document),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [DEMAND_SOURCES_KEY] }),
  });
}

export function useCreateDemandSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ source, fromTemplateId }: { source: Partial<DemandSource>; fromTemplateId?: string }) =>
      demandSourceService.create(source, fromTemplateId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [DEMAND_SOURCES_KEY] }),
  });
}

export function useUpdateDemandSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      source,
      testEvidence,
    }: {
      id: string | number;
      source: Partial<DemandSource>;
      /** Replayed Prove-step evidence (#994). Absent on an ordinary save. */
      testEvidence?: DemandSourceTestEvidence;
    }) => demandSourceService.update(id, source, testEvidence),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [DEMAND_SOURCES_KEY] }),
  });
}

/**
 * **Update a demand source, having first said what that would change elsewhere** (`P4` slice 2) —
 * `useEditImpactGuard` over the one console-side writer, so a guard here cannot be bypassed by a
 * caller that does not know inheritance exists.
 */
export function useGuardedUpdateDemandSource() {
  return useEditImpactGuard(useUpdateDemandSource(), DEMAND_SOURCE_EDIT_IMPACT);
}

const DEMAND_SOURCE_EDIT_IMPACT: EditImpactTarget<{ id: string | number; source: Partial<DemandSource> }> = {
  type: 'demand_source',
  noun: { one: 'source', many: 'sources' },
  target: (vars) => ({ id: vars.id, proposed: vars.source }),
};

export function useDeleteDemandSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string | number) => demandSourceService.delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [DEMAND_SOURCES_KEY] }),
  });
}

export function useToggleDemandSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      enabled,
      source,
    }: {
      id: string | number;
      enabled: boolean;
      source?: { name: string; endpoint: string };
    }) => demandSourceService.toggleEnabled(id, enabled, source),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [DEMAND_SOURCES_KEY] }),
  });
}

/**
 * **Changing a demand source's visibility** (`#1211`). Until this, the value could be set only at
 * creation or through the API, and both surfaces that showed it read the deprecated `isPublic`.
 *
 * A patch carrying `visibility` alone: the server keeps what is stored for anything a patch does not
 * state (`reconcileVisibility`), so this cannot disturb the rest of the record.
 */
export function useSetDemandSourceVisibility() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, visibility }: { id: string | number; visibility: 'public' | 'private' }) =>
      demandSourceService.update(id, { visibility }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [DEMAND_SOURCES_KEY] }),
  });
}

/**
 * A test against a saved source is a **write**: the server records what it observed
 * as that source's readiness (#897). So this invalidates like any other mutation —
 * without it the badge keeps rendering the pre-test state until a reload, and the
 * operator sees a stale claim next to the fresh evidence that contradicts it.
 *
 * Invalidating the key prefix covers both the list and the single-source query, so
 * the detail header and the row update from the same persisted record.
 */
export function useQuickTest() {
  const queryClient = useQueryClient();
  return useMutation<QuickTestResult, Error, { id: string | number; name?: string; context: TestRunContext }>({
    mutationFn: ({ id, name, context }) => demandSourceService.quickTest(id, name, context),
    onSettled: () => queryClient.invalidateQueries({ queryKey: [DEMAND_SOURCES_KEY] }),
  });
}
